import {
  useApiMutation,
  useApiQuery,
  useApiDownloadMutation,
} from "../services/react-query";

export const useGetUsers = (params) =>
  useApiQuery({
    queryOptions: { queryKey: ["users", params] },
    fetchOptions: {
      url: "/users",
      method: "GET",
      params,
    },
  });

export const useGetUser = (id) =>
  useApiQuery({
    queryOptions: { queryKey: ["user", { id }], enabled: !!id },
    fetchOptions: { url: `/users/${id}`, method: "GET" },
  });

export const useGetCurrentUser = () =>
  useApiQuery({
    queryOptions: { queryKey: ["currentUser"] },
    fetchOptions: { url: "/user/self", method: "GET" },
  });

export const useUpdateCurrentUser = () =>
  useApiMutation({
    mutationOptions: { mutationKey: "useUpdateCurrentUser" },
    fetchOptions: { url: "/user/self", method: "PATCH" },
    invalidateQueryKeys: [["currentUser"]],
    successMessage: { title: "Your profile has been updated", color: "green" },
  });

export const useCreateUser = () =>
  useApiMutation({
    mutationOptions: { mutationKey: "useCreateUser" },
    fetchOptions: { url: "/users", method: "POST" },
    invalidateQueryKeys: [["users"]],
    successMessage: { title: "User has been created", color: "green" },
  });

export const useUpdateUser = ({ id, config }) =>
  useApiMutation({
    mutationOptions: { mutationKey: "useUpdateUser" },
    fetchOptions: { url: `/users/${id}`, method: "PATCH" },
    invalidateQueryKeys: [["user", { id }]],
    ...config,
    successMessage: { title: "User has been updated", color: "green" },
  });

export const useDeleteUser = (id) =>
  useApiMutation({
    mutationOptions: { mutationKey: "useDeleteUser" },
    fetchOptions: { url: `/users/${id}`, method: "DELETE" },
    invalidateQueryKeys: [["users"]],
    successMessage: { title: "User has been deleted", color: "green" },
  });

export const useGetUserDocuments = (params) =>
  useApiQuery({
    queryOptions: {
      queryKey: ["userDocuments", params],
      enabled: !!params.userId && !!params.eventId,
    },
    fetchOptions: {
      url: "/user-documents",
      method: "GET",
      params,
    },
  });

export const useOpenPersonalLink = (token) =>
  useApiQuery({
    queryOptions: {
      queryKey: ["openPersonalLink", token],
      enabled: !!token,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    },
    fetchOptions: { url: `/open-documents/${token}`, method: "GET" },
  });

export const useCreateUserDocument = () =>
  useApiMutation({
    mutationOptions: { mutationKey: "useCreateUserDocument" },
    fetchOptions: { url: "/user-documents", method: "POST" },
    invalidateQueryKeys: [["userDocuments"]],
    successMessage: { title: "User document has been created", color: "green" },
  });

export const useDeleteUserDocument = () =>
  useApiMutation({
    mutationOptions: { mutationKey: "useDeleteUserDocument" },
    fetchOptions: { url: `/user-documents`, method: "DELETE" },
    invalidateQueryKeys: [["userDocuments"]],
    successMessage: { title: "User document has been deleted", color: "green" },
  });

export const useCreateDocumentTemplate = () =>
  useApiMutation({
    mutationOptions: { mutationKey: "documentTemplates" },
    fetchOptions: { url: "/document-generator", method: "POST" },
    invalidateQueryKeys: [["documentTemplates"]],
    successMessage: {
      title: "Document template has been created",
      color: "green",
    },
  });

export const useGetDocumentTemplates = (params) =>
  useApiQuery({
    queryOptions: { queryKey: ["documentTemplates", params] },
    fetchOptions: { url: "/document-generator", method: "GET", params },
  });

export const useGetDocumentTemplate = (id) =>
  useApiQuery({
    queryOptions: { queryKey: ["documentTemplate", { id }], enabled: !!id },
    fetchOptions: { url: `/document-generator/${id}`, method: "GET" },
  });

export const useDeleteDocumentTemplate = () =>
  useApiMutation({
    mutationOptions: { mutationKey: "useDeleteDocumentTemplate" },
    fetchOptions: { url: `/document-generator`, method: "DELETE" },
    invalidateQueryKeys: [["documentTemplates"]],
    successMessage: {
      title: "Document template has been deleted",
      color: "green",
    },
  });

export const useGenerateDocument = ({ id }) =>
  useApiDownloadMutation({
    mutationOptions: { mutationKey: ["useGenerateDocument", { id }] },
    fetchOptions: { url: `/document-generator/${id}/generate`, method: "POST" },
    invalidateQueryKeys: [["documentTemplates"]],
    successMessage: {
      title: "Document has been generated",
      color: "green",
    },
  });

export const useGetDistributePreview = ({ id, tagId, enabled = true }) =>
  useApiQuery({
    queryOptions: {
      queryKey: ["distributePreview", { id, tagId }],
      enabled: !!id && enabled,
    },
    fetchOptions: {
      url: `/document-generator/${id}/distribute/preview`,
      method: "GET",
      params: tagId ? { tagId } : undefined,
    },
  });

export const useDistributeDocument = ({ id }) =>
  useApiMutation({
    mutationOptions: { mutationKey: ["documentTemplates", { id }] },
    fetchOptions: {
      url: `/document-generator/${id}/distribute`,
      method: "POST",
    },
    invalidateQueryKeys: [["documentTemplates"]],
    successMessage: {
      title: "Document distributed successfully",
      color: "green",
    },
  });

export const useUpdateDocumentTemplate = ({ id }) =>
  useApiMutation({
    mutationOptions: { mutationKey: "useUpdateDocumentTemplate" },
    fetchOptions: { url: `/document-generator/${id}`, method: "PATCH" },
    invalidateQueryKeys: [["documentTemplate", { id }], ["documentTemplates"]],
    successMessage: {
      title: "Document template has been updated",
      color: "green",
    },
  });

export const useGetEventDocuments = ({ eventId, ...params }) =>
  useApiQuery({
    queryOptions: {
      queryKey: ["eventDocuments", eventId, params],
      enabled: !!eventId,
    },
    fetchOptions: {
      url: `/user-documents/event/${eventId}`,
      method: "GET",
      params,
    },
  });

export const useCreateDistributionAnnouncement = (eventId) =>
  useApiMutation({
    mutationOptions: {
      mutationKey: ["createDistributionAnnouncement", eventId],
    },
    fetchOptions: {
      url: `/user-documents/event/${eventId}/distributions/announcement`,
      method: "POST",
    },
    invalidateQueryKeys: [["eventDistributions", eventId]],
    successMessage: {
      title: "Announcement was created successfully",
      color: "green",
    },
  });

export const useSendDistributionEmails = (eventId) =>
  useApiMutation({
    mutationOptions: {
      mutationKey: ["sendDistributionEmails", eventId],
    },
    fetchOptions: {
      url: `/user-documents/event/${eventId}/distributions/emails`,
      method: "POST",
    },
    invalidateQueryKeys: [["eventDistributions", eventId]],
    successMessage: {
      title: "Emails are being sent",
      color: "green",
    },
  });

export const useGetDistributionEmails = ({
  eventId,
  distributionId,
  ...query
}) =>
  useApiQuery({
    queryOptions: {
      queryKey: ["distributionEmails", eventId, distributionId, query],
      enabled: !!eventId && !!distributionId,
    },
    fetchOptions: {
      url: `/user-documents/event/${eventId}/distributions/${distributionId}/emails`,
      method: "GET",
      params: query,
    },
  });

export const useGetDistributionEmailStats = ({ eventId, distributionId }) =>
  useApiQuery({
    queryOptions: {
      queryKey: ["distributionEmails", eventId, distributionId, "stats"],
      enabled: !!eventId && !!distributionId,
    },
    fetchOptions: {
      url: `/user-documents/event/${eventId}/distributions/${distributionId}/emails/stats`,
      method: "GET",
    },
  });

export const useResendDistributionEmail = ({ eventId, distributionId }) =>
  useApiMutation({
    mutationOptions: {
      mutationKey: ["resendDistributionEmail", eventId, distributionId],
    },
    fetchOptions: {
      url: `/user-documents/event/${eventId}/distributions/${distributionId}/emails/resend`,
      method: "POST",
    },
    invalidateQueryKeys: [["distributionEmails", eventId, distributionId]],
    successMessage: {
      title: "Email is being resent",
      color: "green",
    },
  });

export const useUpdateDistributionEmailRecipient = ({
  eventId,
  distributionId,
  documentId,
}) =>
  useApiMutation({
    mutationOptions: {
      mutationKey: [
        "updateDistributionEmailRecipient",
        eventId,
        distributionId,
        documentId,
      ],
    },
    fetchOptions: {
      url: `/user-documents/event/${eventId}/distributions/${distributionId}/emails/${documentId}`,
      method: "PATCH",
    },
    invalidateQueryKeys: [["distributionEmails", eventId, distributionId]],
    successMessage: {
      title: "Recipient updated",
      color: "green",
    },
  });

export const useDeleteDistribution = (eventId) =>
  useApiMutation({
    mutationOptions: {
      mutationKey: ["deleteDistribution", eventId],
    },
    fetchOptions: {
      url: `/user-documents/event/${eventId}/distributions`,
      method: "DELETE",
    },
    invalidateQueryKeys: [["eventDistributions", eventId]],
    successMessage: {
      title: "Distribution deletion started",
      color: "green",
    },
  });

export const useGetEventDistributions = (eventId) =>
  useApiQuery({
    queryOptions: {
      queryKey: ["eventDistributions", eventId],
      enabled: !!eventId,
    },
    fetchOptions: {
      url: `/user-documents/event/${eventId}/distributions`,
      method: "GET",
    },
  });

export const useBatchUploadUserDocuments = (eventId) =>
  useApiMutation({
    mutationOptions: { mutationKey: ["batchUploadUserDocuments", eventId] },
    fetchOptions: {
      url: `/events/${eventId}/documents/batch-upload`,
      method: "POST",
    },
    invalidateQueryKeys: [["eventDocuments", eventId]],
    successMessage: {
      title: "Documents have been uploaded",
      color: "green",
    },
  });

export const useValidateBatchUploadUserDocuments = (eventId) =>
  useApiMutation({
    mutationOptions: {
      mutationKey: ["validateBatchUploadUserDocuments", eventId],
    },
    fetchOptions: {
      url: `/events/${eventId}/documents/batch-validate`,
      method: "POST",
    },
    invalidateQueryKeys: [["eventDocuments", eventId]],
    successMessage: {
      title: "Documents have been validated",
      color: "green",
    },
  });
